import { isDoorType } from '../utils/visualDesignerSummary';

// Variables removed (feature disabled)
export const DESCRIPTION_VARIABLES = [] as const;

export type DescriptionTemplateContext = {
  orgCode?: string;
  orgName?: string;
  reference?: string;
  customerName?: string;
  siteAddress?: string;
  title?: string;
  lines?: any[];
};

export type DescriptionTemplate = {
  org?: string;
  title: string;
  body?: string;
  /**
   * Optional dynamic builder (used for Visual Designer-driven descriptions, etc).
   * Must return plain text (no {{tokens}}).
   */
  build?: (ctx: DescriptionTemplateContext) => string;
};

function isVisualDesignerLine(line: any): boolean {
  return String(line?.metaJson?.feature || '').trim() === 'visual_designer';
}

function plural(n: number, singular: string, pluralWord?: string) {
  return n === 1 ? singular : (pluralWord || `${singular}s`);
}

function formatCount(n: number, noun: string) {
  return `${n} ${plural(n, noun)}`;
}

function normalizeMaterialName(raw: any): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  // Keep existing branding, but normalize common ids to nice labels
  const lower = s.toLowerCase();
  if (lower === 'upvc') return 'uPVC';
  if (lower === 'aluminium') return 'Aluminium';
  if (lower === 'timber') return 'Timber';
  return s;
}

function joinHuman(items: string[]): string {
  const uniq = Array.from(new Set(items.map(s => String(s || '').trim()).filter(Boolean)));
  if (uniq.length === 0) return '';
  if (uniq.length === 1) return uniq[0];
  if (uniq.length === 2) return `${uniq[0]} and ${uniq[1]}`;
  return `${uniq.slice(0, -1).join(', ')}, and ${uniq[uniq.length - 1]}`;
}

export const DESCRIPTION_TEMPLATES: DescriptionTemplate[] = [
  {
    org: 'KSG',
    title: 'KSG – Supply & install (from Visual Designer items)',
    build: (ctx) => {
      const lines = (ctx?.lines || []).filter(isVisualDesignerLine);
      if (!lines.length) return '';

      let windowsQty = 0;
      let doorsQty = 0;
      const materials: string[] = [];

      for (const l of lines) {
        const qty = Number(l?.qty ?? 1) || 1;
        const meta = l?.metaJson || {};
        const matName = normalizeMaterialName(meta?.window_material?.name || meta?.window_material?.id);
        if (matName) materials.push(matName);

        if (isDoorType(meta?.window_type)) doorsQty += qty;
        else windowsQty += qty;
      }

      const parts: string[] = [];
      if (windowsQty > 0) parts.push(formatCount(windowsQty, 'window'));
      if (doorsQty > 0) parts.push(formatCount(doorsQty, 'door'));

      if (!parts.length) return '';

      const matText = (() => {
        const joined = joinHuman(materials);
        if (!joined) return '';
        const uniqCount = new Set(materials.map(m => m.toLowerCase())).size;
        if (uniqCount <= 1) return `finished in ${joined}`;
        return `finished in mixed materials (${joined})`;
      })();

      return `Supply and installation of ${parts.join(' and ')}${matText ? `, ${matText}` : ''}.`;
    },
  },
  {
    org: 'KSG',
    title: 'KSG – General glazing works',
    body:
      'We propose to carry out glazing works as discussed. All items will be supplied and installed to manufacturer guidance with neat finishing and operation checks.'
  },
  {
    org: 'KSG',
    title: 'KSG – Building Regulations & Finishing Works (include in scope)',
    body: `Finishing and Making Good:
Openings will be finished using suitable insulation, trims and perimeter sealants. Minor localised making good will be carried out where reasonably practical. Further repairs or decorative finishing are excluded unless specifically stated.

Regulatory Compliance:
Where applicable to the agreed scope of works, installations will be carried out with regard to relevant UK Building Regulations, including:

Part L — Conservation of fuel and power / thermal performance
Part F — Ventilation requirements
Part K — Safety glazing to critical locations
Part B — Fire safety and means of escape where relevant
Part A — Structural considerations where openings are altered
Part M — Accessibility considerations for replacement doors where applicable

All works will be carried out by competent installers and, where applicable, registered under the CERTASS self-certification scheme, providing certification of compliance upon completion.`
  }
];


